import { Amount } from './amount';
import { AssetTypeEnum } from './instrument';
import { LoadingStatus } from './loadingStatus';

export type PortfolioNumber = string;
export type FavoriteNumbers = string[];
export type CashAccountId = string;
export type CurrentNumber = string;
export type AssetPercentages = { [assetType: string]: number };

export interface Transaction {
  date: Date;
  bookingText: string;
  amount: Amount;
  cashAccountId: string;
  orderType?: TransactionOrderTypeEnum;
  orderId?: number;
}

export enum TransactionOrderTypeEnum {
  TRANSFER_IN = 'TRIN',
  TRANSFER_OUT = 'TROU',
  INSTANT_PAYMENT_IN = 'IPIN',
  ACCOUNT_ADMIN_FEES = 'AFAA',
  CUSTODY_FEES = 'CUFE',
  DEBIT_INTEREST = 'CLOS',
  ACCOUNT_TO_ACCOUNT_TRANSFER = 'ATOA',
  BUY = 'AC_PURCHASE',
  SELL = 'VE_SELL',
  SUBSCRIPTION = 'SC_SUBSCRIPTION',
  REDEMPTION = 'RD_REDEMPTION'
}

export interface Performances {
  currentYearPerformance: Performance;
  previousYearPerformance: Performance;
  inceptionPerformance: Performance;
}

export interface Performance extends PerformancePeriod {
  portfolioId?: string;
  currencyId?: string;
  performanceMethod?: PerformanceMethodEnum;
  performanceSubPeriods?: PerformancePeriod[];
}
export interface PerformancePeriod {
  beginDate?: Date;
  endDate?: Date;
  netBeginAmount?: number;
  totalFlow?: number;
  performanceAmount?: number;
  performanceRate?: number;
  cumulatedPerformanceRate?: number;
  netEndAmount?: number;
}

export enum PerformanceFrequency {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL'
}

export enum PerformanceMethodEnum {
  MWR = 'MWR',
  TWR = 'TWR',
  DAILY = 'DAILY',
  LINKED_SECURITY = 'LINKED_SECURITY'
}

export enum PerformanceEnum {
  CURRENT_YEAR = 'CURRENT_YEAR',
  PREVIOUS_YEAR = 'PREVIOUS_YEAR',
  HISTORICAL = 'HISTORICAL',
  INCEPTION = 'INCEPTION'
}

export enum PerformanceGraphTypeEnum {
  LINE = 'LINE',
  BAR = 'BAR'
}

export interface Portfolio {
  portfolioNumber: PortfolioNumber;
  name?: string;
  total?: Amount;
  portfolioTypeId?: string;
  yTDinOutflow?: Amount;
  assetAllocations?: AssetAllocation[];
  performance?: Performances;
  relatedUserIds?: string[];
  favorite?: boolean;
  loadingStatus?: LoadingStatus;
  portfolioMandateTypeId?: string;
  depositaryBankId?: string;
}

export interface AssetAllocation {
  id?: AssetAllocationId;
  description?: string;
  total?: Amount;
  instruments?: PortfolioInstrument[];
  ranking?: number;
}

export interface CashAllocation extends AssetAllocation {
  fiduciaryDeposits?: Amount;
  forexForwards?: Amount;
  accounts?: CashAccount[];
}

export interface PortfolioInstrument {
  title?: string;
  units?: number;
  commitment?: number;
  price?: Amount;
  priceDate?: string;
  positionId?: number;
  securityId?: number;
  averagePurchasePrice?: number;
  capitalCalled?: number;
  capitalDistributed?: number;
  initialPurchaseDate?: Date;
  openCommitment?: number;
  iSIN?: string;
  yieldToMaturity?: number;
  performance?: number;
  assetType?: AssetTypeEnum;
  isTradingAllowed?: boolean;
  accruedInterests?: number;
  amount?: Amount;
  positionType?: PositionTypeEnum;
  ticker?: string;
  shortDescription?: string;
  classificationId?: string;
}

export enum PositionTypeEnum {
  SECURITY = 'SECURITY',
  ACCOUNT = 'ACCOUNT',
  FIDUCIARY = 'FIDUCIARY',
  EXCHANGE = 'EXCHANGE',
  TREASURY = 'TREASURY',
  CONTRACT = 'CONTRACT'
}

export enum AssetAllocationId {
  CASH = 'CASH',
  EQUITIES = 'EQUITIES',
  FIXED_INCOME = 'FIXED_INCOME',
  COMMODITIES = 'COMMODITIES',
  ALTERNATIVES = 'ALTERNATIVES',
  HOLDINGS = 'HOLDINGS',
  LOANS = 'LOANS',
  GUARANTEES = 'GUARANTEES',
  // Level 1 IDs below, used only by CGE
  CASH_LEVEL1 = 'CA',
  EQUITIES_LEVEL1 = 'EQ',
  FIXED_INCOME_LEVEL1 = 'FI',
  COMMODITIES_LEVEL1 = 'CO',
  SHORT_TERM_LEVEL1 = 'ST',
  FX_FORWARDS_LEVEL1 = 'CF',
  MIXED_FUNDS_LEVEL1 = 'MX',
  ALTERNATIVES_LEVEL1 = 'AI',
  DERIVATIVES_LEVEL1 = 'DE',
  STRUCTURED_PRODUCTS_LEVEL1 = 'SP',
  PRIVATE_EQUITY_LEVEL1 = 'PE',
  DIGITAL_ASSETS_LEVEL1 = 'DA',
  REAL_ESTATE_LEVEL1 = 'RE',
  LIABILITIES_LEVEL1 = 'LI',
  OTHERS_LEVEL1 = 'OT'
}

export interface CashAccount {
  id?: CashAccountId;
  totalInPortfolioCurrency?: Amount;
  transactions?: Transaction[];
  current?: boolean;
  isLoading?: boolean;
  total?: Amount;
  description?: string;
}

export interface DepositaryMobileBank {
  id?: string;
  identification?: string;
  description?: string;
  shortDescription?: string;
}
